import { Menu, X, Search, ArrowLeft } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import visigalogo from "../assets/visigalogo.png";
import ThemeToggle from "./ThemeToggle";
import LanguageToggle from "./LanguageToggle";
import { useTranslation } from "react-i18next";

// ✅ Import react-icons
import { FaTiktok, FaYoutube, FaInstagram } from "react-icons/fa";

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const drawerRef = useRef(null);
  const searchRef = useRef(null);

  const navItems = [
    { label: t("navbar.home"), href: "/" },
    {
      label: t("navbar.aboutUs"),
      dropdown: [
        { label: t("navbar.aboutSchool"), href: "/about-us" },
        { label: t("navbar.messageFromRector"), href: "/msg" },
        { label: t("navbar.staff"), href: "/staff" },
        { label: t("navbar.seminaryHistory"), href: "/history" },
        { label: t("navbar.organizationStructure"), href: "/organization" },
      ],
    },
    {
      label: t("navbar.services"),
      dropdown: [
        { label: t("navbar.oLevel"), href: "/o-level" },
        { label: t("navbar.aLevel"), href: "/a-level" },
        { label: t("navbar.spiritualService"), href: "/spiritual" },
        { label: t("navbar.gamesAndSports"), href: "/games" },
        { label: t("navbar.dispensaryService"), href: "/dispensary" },
      ],
    },
    {
      label: t("navbar.work"),
      dropdown: [
        { label: t("navbar.work1"), href: "/works" },
        { label: t("navbar.work2"), href: "/work" },
      ],
    },
    {
      label: t("navbar.admission"),
      dropdown: [
        { label: t("navbar.oLevelAdmission"), href: "/o-level-admission" },
        { label: t("navbar.aLevelAdmission"), href: "/a-level-admission" },
      ],
    },
    { label: t("navbar.alumni"), href: "/alumni" },
    { label: t("navbar.news"), href: "/news" },
    { label: t("navbar.contact"), href: "/contact" },
  ];

  // Search functionality
  const searchableContent = [
    { title: "Home", content: "home visiga seminary", href: "/" },
    { title: "About Us", content: "about school seminary history staff organization", href: "/about-us" },
    { title: "Message from Rector", content: "rector message leadership", href: "/msg" },
    { title: "Staff", content: "staff teachers faculty", href: "/staff" },
    { title: "Seminary History", content: "history background heritage", href: "/history" },
    { title: "Organization Structure", content: "organization structure management", href: "/organization" },
    { title: "O-Level", content: "o level ordinary education", href: "/o-level" },
    { title: "A-Level", content: "a level advanced education", href: "/a-level" },
    { title: "Spiritual Service", content: "spiritual service religious", href: "/spiritual" },
    { title: "Games and Sports", content: "games sports activities", href: "/games" },
    { title: "Dispensary Service", content: "dispensary health medical", href: "/dispensary" },
    { title: "Works", content: "works projects activities", href: "/works" },
    { title: "Work", content: "work employment", href: "/work" },
    { title: "O-Level Admission", content: "admission o level enrollment", href: "/o-level-admission" },
    { title: "A-Level Admission", content: "admission a level enrollment", href: "/a-level-admission" },
    { title: "Alumni", content: "alumni graduates former students", href: "/alumni" },
    { title: "News", content: "news announcements updates", href: "/news" },
    { title: "Contact", content: "contact information address phone", href: "/contact" },
    { title: "Wawata", content: "wawata (wanawake wakatoliki Tanzania) ", href: "/wawata" },
    { title: "Services", content: "wawata (Services provided by seminary) ", href: "/services" },
    { title: "Pillars", content: "wawata (Pillars  of the  seminary) ", href: "/Pillars" },
    { title: "Gallery", content: "wawata (Gallery or images  of the seminary) ", href: "/gallery" },
  ];

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() === "") {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const filtered = searchableContent.filter(item =>
      item.title.toLowerCase().includes(query.toLowerCase()) ||
      item.content.toLowerCase().includes(query.toLowerCase())
    );
    
    setSearchResults(filtered);
    setShowSearchResults(true);
  };

  const handleSearchSelect = (href, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSearchQuery("");
    setShowSearchResults(false);
    // Scroll to top immediately for better UX
    window.scrollTo(0, 0);
    // Use setTimeout to ensure state updates complete before navigation
    setTimeout(() => {
      navigate(href);
    }, 0);
  };

  // Toggle functions
  const toggleNavbar = () => setMobileDrawerOpen(!mobileDrawerOpen);
  const toggleMobileDropdown = (index) =>
    setMobileDropdownOpen(mobileDropdownOpen === index ? null : index);

  // Dropdown hover controls
  const handleMouseEnter = (key) => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setActiveDropdown(key);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => setActiveDropdown(null), 200);
    setHoverTimeout(timeout);
  };

  // Check if we're on the home page
  const isHomePage = location.pathname === "/";

  // Handle back button click
  const handleBackClick = () => {
    navigate(-1);
  };

  // Close navbar and search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileDrawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        setMobileDrawerOpen(false);
      }
      
      if (
        showSearchResults &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (hoverTimeout) clearTimeout(hoverTimeout);
    };
  }, [mobileDrawerOpen, hoverTimeout, showSearchResults]);

  return (
    <nav className="sticky top-0 z-50 py-2 bg-white dark:bg-gray-900 border-b border-neutral-300 dark:border-neutral-700 shadow-md">
      <div className="container px-2  mx-auto relative">
        <div className="flex justify-between items-center px-2 lg:px-6 py-1 border-b border-neutral-200 dark:border-neutral-700">
          {/* Left: Back Button + Logo + Title */}
          <div className="flex items-center flex-shrink-0 gap-2">
            {/* Back Button - Only show when not on home page */}
            {!isHomePage && (
              <button
                onClick={handleBackClick}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center justify-center"
                title="Go back"
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5 text-black dark:text-white" />
              </button>
            )}
            <Link to="/" className="flex items-center">
              <img src={visigalogo} alt="Logo" className="h-10 w-10 mr-2" />
              <span className="text-base font-bold text-black dark:text-white hidden sm:block">
                Visiga Seminary
              </span>
            </Link>
          </div>

          {/* Center: Search and Quick Links */}
          <div className="hidden lg:flex items-center space-x-4 flex-1 justify-center max-w-2xl">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md z-[100]" ref={searchRef}>
              <div className="relative z-[100]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-[101]" />
                <input
                  type="text"
                  placeholder="Search website..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent relative z-[101]"
                />
              </div>
            </div>
            
            {/* Search Results Dropdown - Positioned outside to avoid z-index conflicts */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="fixed top-[120px] left-1/2 transform -translate-x-1/2 w-96 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto">
                {searchResults.map((result, index) => (
                  <button
                    key={index}
                    onClick={(event) => handleSearchSelect(result.href, event)}
                    onMouseDown={(event) => event.preventDefault()}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600 last:border-b-0 cursor-pointer block"
                  >
                    <div className="font-medium text-black dark:text-white">{result.title}</div>
                  </button>
                ))}
              </div>
            )}

            {/* Quick Links */}
            <div className="flex items-center space-x-3">
              <Link
                to="/news"
                className="bg-blue-700 text-white px-3 py-1.5 text-xs font-semibold rounded hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                News
              </Link>
              {/* <Link to="/contact" className="text-blue-700 text-xs hover:underline">
                📩 CONTACT
              </Link>
              <Link to="/contact" className="text-blue-700 text-xs hover:underline">
                📩 ALUMNI
              </Link> */}
            </div>
          </div>

          {/* Right: Controls + Social Icons */}
          <div className="hidden lg:flex items-center space-x-2 flex-shrink-0">
            <LanguageToggle />
            <ThemeToggle />
            <div className="flex items-center space-x-1">
              <a
                href="https://www.tiktok.com/@visigaseminary?_t=ZM-8yq9jAQpwRB&_r=1"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-700 text-white p-1.5 rounded text-xs hover:bg-red-800 transition-colors"
              >
                <FaTiktok />
              </a>
              <a
                href="https://youtube.com/@visigaseminarytv?feature=shared"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-700 text-white p-1.5 rounded text-xs hover:bg-red-800 transition-colors"
              >
                <FaYoutube />
              </a>
              <a
                href="https://www.instagram.com/visigaseminary?igsh=MWhveWlxbGU1NWRoag=="
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-700 text-white p-1.5 rounded text-xs hover:bg-red-800 transition-colors"
              >
                <FaInstagram />
              </a>
            </div>
          </div>

          {/* Mobile Menu Button + Search */}
          <div className="lg:hidden flex items-center space-x-2">
            {/* Mobile Search */}
            <div className="relative" ref={searchRef}>
              <Search 
                className="h-5 w-5 text-gray-600 dark:text-gray-300 cursor-pointer"
                onClick={() => setShowSearchResults(!showSearchResults)}
              />
              {showSearchResults && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50">
                  <input
                    type="text"
                    placeholder="Search website..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border-b border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-black dark:text-white focus:outline-none"
                    autoFocus
                  />
                  {searchResults.length > 0 && (
                    <div className="max-h-48 overflow-y-auto">
                      {searchResults.map((result, index) => (
                        <button
                          key={index}
                          onClick={(event) => handleSearchSelect(result.href, event)}
                          onMouseDown={(event) => event.preventDefault()}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600 last:border-b-0 cursor-pointer"
                        >
                          <div className="font-medium text-black dark:text-white text-sm">{result.title}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <button
              onClick={toggleNavbar}
              className="p-2 rounded-md"
            >
              {mobileDrawerOpen ? (
                <X className="h-6 w-6 text-black dark:text-white" />
              ) : (
                <Menu className="h-6 w-6 text-black dark:text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:block">
          <ul className="flex justify-center items-center space-x-2 text-black dark:text-white text-sm font-medium py-2">
            {navItems.map((item, index) => {
              const dropdownKey = item.label.toLowerCase().replace(/\s+/g, "-");
              return (
                <li
                  key={index}
                  className="relative list-none"
                  onMouseEnter={() =>
                    item.dropdown && handleMouseEnter(dropdownKey)
                  }
                  onMouseLeave={handleMouseLeave}
                >
                  {item.dropdown ? (
                    <>
                      <button
                        className="flex items-center space-x-1 px-3 py-1.5 rounded-md border border-gray-300 dark:border-neutral-600 bg-gray-100 dark:bg-neutral-800 hover:bg-gray-200 dark:hover:bg-neutral-700 focus:outline-none transition-colors whitespace-nowrap text-sm"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === dropdownKey}
                      >
                        <span className="whitespace-nowrap">{item.label}</span>
                        <span className="text-xs">▼</span>
                      </button>

                      {activeDropdown === dropdownKey && (
                        <ul className="absolute left-0 mt-1 w-52 bg-white dark:bg-neutral-800 shadow-lg border rounded-md z-50 py-1">
                          {item.dropdown.map((subItem, subIndex) => (
                            <li key={subIndex} className="list-none">
                              <Link
                                to={subItem.href}
                                className="block w-full text-left py-2 px-3 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-md text-sm"
                                onClick={() => setActiveDropdown(null)}
                              >
                                {subItem.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className="block px-3 py-1.5 rounded-md border border-gray-300 dark:border-neutral-600 bg-gray-100 dark:bg-neutral-800 hover:bg-gray-200 dark:hover:bg-neutral-700 transition-colors text-sm whitespace-nowrap"
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>



        {/* Mobile Menu Drawer */}
        {mobileDrawerOpen && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
              onClick={toggleNavbar}
            ></div>
            <div
              ref={drawerRef}
              className="fixed right-0 top-0 h-full w-64 bg-white dark:bg-neutral-900 text-black dark:text-white z-50 p-6 flex flex-col shadow-lg overflow-y-auto"
            >
              {/* Drawer Header */}
              <div className="flex justify-between items-center mb-6">
                <Link
                  to="/"
                  className="flex items-center"
                  onClick={toggleNavbar}
                >
                  <img className="h-8 w-8 mr-2" src={visigalogo} alt="Logo" />
                  <span className="text-lg font-medium text-black dark:text-white">
                    Visiga Seminary
                  </span>
                </Link>
                <button
                  onClick={toggleNavbar}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-800"
                >
                  <X className="text-black dark:text-white h-6 w-6" />
                </button>
              </div>

              {/* Drawer Links */}
              <ul className="space-y-2">
                {navItems.map((item, index) => (
                  <li key={index} className="list-none">
                    {item.dropdown ? (
                      <>
                        <button
                          onClick={() => toggleMobileDropdown(index)}
                          className="flex justify-between items-center w-full py-2 px-2 text-left hover:bg-gray-50 dark:hover:bg-neutral-800 rounded-md"
                        >
                          <span>{item.label}</span>
                          <span className="transform transition-transform">
                            {mobileDropdownOpen === index ? "−" : "+"}
                          </span>
                        </button>
                        {mobileDropdownOpen === index && (
                          <ul className="ml-4 mt-1 space-y-1">
                            {item.dropdown.map((subItem, subIndex) => (
                              <li key={subIndex}>
                                <Link
                                  to={subItem.href}
                                  className="block py-2 px-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-md"
                                  onClick={toggleNavbar}
                                >
                                  {subItem.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className="block py-2 px-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-md"
                        onClick={toggleNavbar}
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
                <ThemeToggle />
                <LanguageToggle />
              </ul>
            </div>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
